import { useEffect, useState, useCallback, useRef } from 'react';
import BinaryOrbVisual from '@/components/portal/0Logic/BinaryOrbVisual';

export default function BinaryOrb({ status = 'idle', size = 64 }) {
  const [lights, setLights] = useState([]);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(0);
  const containerRef = useRef(null);
  const visibleRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const statusColors = {
    idle: ['#3b82f6', '#06b6d4', '#0ea5e9', '#60a5fa'],
    listening: ['#10b981', '#34d399', '#6ee7b7', '#34d399'],
    processing: ['#f59e0b', '#fbbf24', '#fcd34d', '#fbbf24'],
    speaking: ['#a855f7', '#c084fc', '#d8b4fe', '#c084fc']
  };

  const createLight = useCallback((index) => {
    const colors = statusColors[status] || statusColors.idle;

    return {
      id: index,
      x: 35 + Math.random() * 30,
      y: 35 + Math.random() * 30,
      vx: (Math.random() - 0.5) * 0.015,
      vy: (Math.random() - 0.5) * 0.015,
      radius: size * (0.12 + Math.random() * 0.08),
      color: colors[index % colors.length],
      targetColor: colors[Math.floor(Math.random() * colors.length)],
      colorProgress: 0,
      opacity: 0.5 + Math.random() * 0.35,
      targetOpacity: 0.5 + Math.random() * 0.35,
      opacitySpeed: 0.005 + Math.random() * 0.006,
      fadeDirection: Math.random() > 0.5 ? 1 : -1
    };
  }, [status, size]);

  const updateLights = useCallback((currentTime) => {
    animationRef.current = requestAnimationFrame(updateLights);
    if (!visibleRef.current || document.hidden || currentTime - lastTimeRef.current < 16) return;
    lastTimeRef.current = currentTime;

    setLights((prevLights) => {
      if (prevLights.length === 0) return prevLights;

      return prevLights.map((light) => {
        let newX = light.x + light.vx * 40;
        let newY = light.y + light.vy * 40;
        let newVx = light.vx;
        let newVy = light.vy;

        if (newX <= 30) {
          newVx = Math.abs(newVx) * (0.9 + Math.random() * 0.05);
          newX = 30 + Math.abs(newX - 30) * 0.3;
        } else if (newX >= 70) {
          newVx = -Math.abs(newVx) * (0.9 + Math.random() * 0.05);
          newX = 70 - Math.abs(newX - 70) * 0.3;
        }

        if (newY <= 30) {
          newVy = Math.abs(newVy) * (0.9 + Math.random() * 0.05);
          newY = 30 + Math.abs(newY - 30) * 0.3;
        } else if (newY >= 70) {
          newVy = -Math.abs(newVy) * (0.9 + Math.random() * 0.05);
          newY = 70 - Math.abs(newY - 70) * 0.3;
        }

        if (Math.random() < 0.0015) {
          newVx = (Math.random() - 0.5) * 0.03;
          newVy = (Math.random() - 0.5) * 0.03;
        }

        let newColorProgress = light.colorProgress + 0.0015;
        let newColor = light.color;
        let targetColor = light.targetColor;
        if (newColorProgress >= 1) {
          newColor = light.targetColor;
          const colors = statusColors[status] || statusColors.idle;
          targetColor = colors[Math.floor(Math.random() * colors.length)];
          newColorProgress = 0;
        }

        let newOpacity = light.opacity + light.fadeDirection * light.opacitySpeed;
        let newFadeDirection = light.fadeDirection;
        if (newOpacity >= 0.85) {
          newFadeDirection = -1;
          newOpacity = 0.85;
        } else if (newOpacity <= 0.3) {
          newFadeDirection = 1;
          newOpacity = 0.3;
        }

        return {
          ...light,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy,
          color: newColor,
          targetColor,
          colorProgress: newColorProgress,
          opacity: newOpacity,
          fadeDirection: newFadeDirection
        };
      });
    });
  }, [status]);

  useEffect(() => {
    const initialLights = Array.from({ length: 5 }, (_, i) => createLight(i));
    setLights(initialLights);

    animationRef.current = requestAnimationFrame(updateLights);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [createLight, updateLights]);

  useEffect(() => {
    const colors = statusColors[status] || statusColors.idle;
    setLights((prev) => prev.map((light, i) => ({
      ...light,
      targetColor: colors[i % colors.length]
    })));
  }, [status]);

  return <BinaryOrbVisual lights={lights} size={size} containerRef={containerRef} />;
}